import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";

const TOOLS = [
  { id: "ip-lookup", name: "IP Lookup", description: "Geolocation, ISP, ASN, abuse data for an IP", endpoint: "/tools/ip-lookup", input: { ip: "" }, gated: null,
    placeholder: { ip: "8.8.8.8" }, fields: [["ip","IP address"]] },
  { id: "whois", name: "WHOIS Lookup", description: "Domain registration data", endpoint: "/tools/whois", input: { domain: "" }, gated: "whoisxmlapi",
    placeholder: { domain: "example.com" }, fields: [["domain","Domain"]] },
  { id: "dns", name: "DNS Lookup", description: "A, AAAA, MX, TXT, NS, CNAME records", endpoint: "/tools/dns", input: { domain: "" }, gated: null,
    placeholder: { domain: "example.com" }, fields: [["domain","Domain"]] },
  { id: "reverse-ip", name: "Reverse IP", description: "Domains sharing an IP", endpoint: "/tools/reverse-ip", input: { ip: "" }, gated: null,
    placeholder: { ip: "104.21.0.1" }, fields: [["ip","IP"]] },
  { id: "ssl-check", name: "SSL Certificate", description: "Validity, expiry, issuer", endpoint: "/tools/ssl-check", input: { domain: "" }, gated: null,
    placeholder: { domain: "example.com" }, fields: [["domain","Domain"]] },
  { id: "url-scan", name: "URL / Link Scanner", description: "VirusTotal multi-engine reputation", endpoint: "/tools/url-scan", input: { url: "" }, gated: "virustotal",
    placeholder: { url: "https://" }, fields: [["url","URL"]] },
  { id: "email-headers", name: "Email Header Analyzer", description: "SPF / DKIM / DMARC + delivery path", endpoint: "/tools/email-headers", input: { raw: "" }, gated: null, textarea: "raw",
    placeholder: { raw: "Paste full email headers…" }, fields: [["raw","Raw headers"]] },
  { id: "breach-check", name: "Breach Check", description: "HaveIBeenPwned exposure", endpoint: "/tools/breach-check", input: { email: "" }, gated: "haveibeenpwned",
    placeholder: { email: "name@example.com" }, fields: [["email","Email"]] },
  { id: "hash", name: "Hash Generator", description: "MD5 / SHA-1 / SHA-256 / SHA-512", endpoint: "/tools/hash", input: { text: "" }, gated: null,
    placeholder: { text: "Text to hash" }, fields: [["text","Plain text"]] },
  { id: "base64", name: "Base64 Encode / Decode", description: "Round-trip encoder", endpoint: "/tools/base64", input: { text: "", mode: "encode" }, gated: null,
    placeholder: { text: "" }, fields: [["text","Text"]] },
  { id: "subnet", name: "Subnet Calculator", description: "Network / broadcast / usable hosts", endpoint: "/tools/subnet", input: { cidr: "" }, gated: null,
    placeholder: { cidr: "192.168.1.0/24" }, fields: [["cidr","CIDR"]] },
];

function ResultViewer({ data }) {
  if (!data) return null;
  return (
    <pre className="mt-3 p-3 bg-[#050d1a] border border-[rgba(0,170,255,0.18)] rounded text-[11px] font-mono-tech text-[#94a3b8] overflow-auto max-h-72">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function ToolCard({ tool, status }) {
  const [input, setInput] = useState({ ...tool.input });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isGated = tool.gated && status && !status[tool.gated]?.active;

  const run = async () => {
    setError(""); setResult(null); setLoading(true);
    try {
      const { data } = await api.post(tool.endpoint, input);
      if (data.status === "unavailable") {
        setError(`API key not configured — set ${data.env} in .env to enable.`);
      } else {
        setResult(data);
      }
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="csc-card p-5 flex flex-col" data-testid={`tool-${tool.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]">{tool.id}</div>
          <h3 className="text-base font-semibold mt-1">{tool.name}</h3>
          <p className="text-xs text-[#94a3b8] mt-1">{tool.description}</p>
        </div>
        {isGated && (
          <span className="font-mono-tech text-[9px] uppercase tracking-[0.22em] px-2 py-1 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 whitespace-nowrap">
            Coming soon
          </span>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {tool.fields.map(([k, label]) => (
          <div key={k}>
            <label className="csc-label">{label}</label>
            {tool.textarea === k ? (
              <textarea
                rows={5}
                value={input[k] || ""}
                onChange={(e) => setInput({ ...input, [k]: e.target.value })}
                placeholder={tool.placeholder[k]}
                disabled={isGated}
                className="csc-input resize-none font-mono-tech text-xs"
                data-testid={`${tool.id}-input-${k}`}
              />
            ) : (
              <input
                value={input[k] || ""}
                onChange={(e) => setInput({ ...input, [k]: e.target.value })}
                placeholder={tool.placeholder[k]}
                disabled={isGated}
                className="csc-input"
                data-testid={`${tool.id}-input-${k}`}
              />
            )}
          </div>
        ))}
        {tool.id === "base64" && (
          <div className="flex items-center gap-2 text-[11px] font-mono-tech uppercase tracking-wider text-[#94a3b8]">
            {["encode", "decode"].map((m) => (
              <label key={m} className="flex items-center gap-1 cursor-pointer">
                <input type="radio" name={`${tool.id}-mode`} value={m} checked={input.mode === m}
                  onChange={() => setInput({ ...input, mode: m })} className="accent-[#00aaff]" />
                {m}
              </label>
            ))}
          </div>
        )}
      </div>

      <button onClick={run} disabled={loading || isGated} className="csc-btn-primary !py-2 mt-3" data-testid={`${tool.id}-run`}>
        {loading ? "Running…" : isGated ? "Disabled" : "Run"}
      </button>

      {error && <div className="text-xs text-red-400 mt-2" data-testid={`${tool.id}-error`}>{error}</div>}
      <ResultViewer data={result} />
    </div>
  );
}

function PortScanner() {
  const [target, setTarget] = useState("");
  const [confirmAuth, setConfirmAuth] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    setError(""); setResult(null); setLoading(true);
    try {
      const { data } = await api.post("/tools/port-scan", { target, confirm_authorization: confirmAuth });
      setResult(data);
      setConfirmAuth(false);
    } catch (e) { setError(formatApiError(e)); setConfirmAuth(false); }
    finally { setLoading(false); }
  };

  const exportTxt = () => {
    if (!result) return;
    const txt = result.results.map((r) => `${r.port}\t${r.service}\t${r.status}`).join("\n");
    const blob = new Blob([`Port scan for ${result.target} (${result.ip})\n\n${txt}\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `port_scan_${result.target}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="csc-card p-5 col-span-1 md:col-span-2 lg:col-span-3" data-testid="tool-port-scan">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]">port-scan</div>
          <h3 className="text-base font-semibold mt-1">Port Scanner (12 common ports)</h3>
          <p className="text-xs text-[#94a3b8] mt-1">21/22/23/25/53/80/110/143/443/465/587/993 · 1 scan / 30s / user · 10/day</p>
        </div>
      </div>
      <div className="mt-3 p-3 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs">
        ⚠️ Use responsibly — only scan systems you own or have explicit written permission to test. Unauthorized scanning may be illegal in your jurisdiction.
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="csc-label">Target IP or domain</label>
          <input value={target} onChange={(e) => setTarget(e.target.value)} className="csc-input" placeholder="example.com or 192.168.1.1" data-testid="port-scan-target" />
        </div>
        <label className="flex items-center gap-2 text-xs text-[#94a3b8] cursor-pointer">
          <input type="checkbox" checked={confirmAuth} onChange={(e) => setConfirmAuth(e.target.checked)} className="accent-[#39ff14]" data-testid="port-scan-confirm" />
          I confirm I have authorization to scan this target.
        </label>
      </div>
      <button onClick={run} disabled={!target || !confirmAuth || loading} className="csc-btn-primary mt-3" data-testid="port-scan-run">
        {loading ? "Scanning…" : "Scan"}
      </button>
      {error && <div className="text-xs text-red-400 mt-2" data-testid="port-scan-error">{error}</div>}
      {result && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono-tech text-[11px] text-[#94a3b8]">{result.target} → {result.ip} {result.is_private && <span className="text-[#39ff14] ml-2">[private network]</span>}</div>
            <button onClick={exportTxt} className="csc-btn-ghost !py-1.5 !px-2.5">Export .txt</button>
          </div>
          <table className="w-full text-xs">
            <thead className="font-mono-tech uppercase tracking-wider text-[#94a3b8]">
              <tr><th className="text-left py-1">Port</th><th className="text-left py-1">Service</th><th className="text-left py-1">Status</th></tr>
            </thead>
            <tbody>
              {result.results.map((r) => (
                <tr key={r.port} className="border-t border-[rgba(0,170,255,0.18)]">
                  <td className="py-1 font-mono-tech text-white">{r.port}</td>
                  <td className="py-1">{r.service}</td>
                  <td className={`py-1 ${r.status === "Open" ? "text-[#39ff14]" : r.status === "Closed" ? "text-[#94a3b8]" : "text-amber-300"}`}>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function InvestigationTools() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/tools/api-status").then((r) => setStatus(r.data)).catch(() => setStatus({}));
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="investigation-tools">
      {TOOLS.map((t) => <ToolCard key={t.id} tool={t} status={status} />)}
      <PortScanner />
    </div>
  );
}
